/**
 * Legal — Privacy Policy Template per chi installa Aria Widget
 *
 * Testo pronto da copiare/incollare nella Privacy Policy del proprio sito
 * per dichiarare l'uso del widget Aria conforme al GDPR.
 *
 * Path: /legal/privacy-template-widget
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";

const TEMPLATE = `## Assistente conversazionale "Aria"

Sul nostro sito è installato un assistente conversazionale chiamato "Aria", fornito da TotalKlick srls, che ci consente di rispondere in modo automatico alle domande dei visitatori e di raccogliere richieste di contatto.

### Quali dati raccoglie Aria

Aria può raccogliere due tipologie di dati:

**Dati anonimi (raccolti automaticamente, base giuridica: interesse legittimo — Art. 6.1.f GDPR):**
- Identificatore di sessione (non persistente, non collegato a cookie tracking)
- Contenuto della conversazione che intrattieni con Aria
- Pagina del nostro sito da cui hai aperto la chat
- Country code (es. IT, FR) derivato dall'header HTTP fornito dal nostro CDN
- Prefisso IP (/16) — pseudonimizzato per analisi anti-spam
- Numero di messaggi e durata della sessione

Questi dati sono utilizzati per migliorare il servizio e fornirti risposte più pertinenti. Sono conservati per **30 giorni** e poi cancellati automaticamente.

**Dati identificativi (raccolti solo con il tuo consenso esplicito — Art. 6.1.a GDPR):**
Se decidi di lasciare un contatto attraverso il form integrato in Aria, ti verrà mostrato un avviso esplicito con casella di consenso (NON pre-spuntata) prima di salvare:
- Nome e cognome
- Email
- Numero di telefono
- IP completo
- Città (se geolocalizzabile)
- Testo del consenso firmato + timestamp

Questi dati sono conservati per **24 mesi** dall'ultimo contatto utile, salvo tu richieda la cancellazione.

### A chi vengono comunicati i tuoi dati

I tuoi dati sono trattati da:
- **[INSERIRE NOME AZIENDA]**, Titolare del trattamento, in qualità di gestore di questo sito.
- **TotalKlick srls**, Responsabile del trattamento, per la fornitura tecnica del servizio Aria. Vedi [DPA Aria Widget](https://totalklick.it/legal/dpa-aria-widget).
- **Sub-responsabili** tecnici utilizzati da TotalKlick (Anthropic per il modello AI, MongoDB per il database, Cloudflare per la sicurezza). L'elenco completo dei sub-responsabili è nel DPA.

I dati possono essere trasferiti negli **Stati Uniti** verso fornitori sopra elencati, sulla base delle **Standard Contractual Clauses (SCC)** approvate dalla Commissione UE.

### I tuoi diritti

Hai il diritto di:
- **Accedere** ai tuoi dati (Art. 15 GDPR) — scrivici a [INSERIRE EMAIL PRIVACY]
- **Rettificare** dati errati (Art. 16)
- **Cancellare** i tuoi dati (Art. 17 — "diritto all'oblio")
- **Limitare** il trattamento (Art. 18)
- **Portabilità** dei dati in formato strutturato (Art. 20)
- **Opporti** al trattamento per motivi legittimi (Art. 21)
- **Revocare il consenso** in qualsiasi momento (Art. 7.3)
- **Reclamare al Garante Privacy** ([garanteprivacy.it](https://www.garanteprivacy.it))

Per esercitare i diritti, scrivi a **[INSERIRE EMAIL PRIVACY DEL CLIENTE]** o usa il form di richiesta dati su questo sito.

### Periodo di conservazione

- Conversazioni anonime: **30 giorni** (cancellazione automatica).
- Lead identificati: **24 mesi** dall'ultimo contatto, poi richiesta rinnovo consenso.
- Log di audit (cancellazioni, accessi): **5 anni** per obblighi legali.

### Cookie usati da Aria

Aria **NON utilizza cookie di tracciamento**. L'identificatore di sessione vive solo in memoria del browser (sessionStorage) e si cancella alla chiusura della scheda.

### Misure di sicurezza

I tuoi dati sono protetti con:
- Cifratura TLS 1.3 in transito
- Cifratura AES-256 a riposo
- Hashing bcrypt per credenziali amministrative
- Pseudonimizzazione degli IP nei dati anonimi
- Audit log di tutti gli accessi privilegiati
- Backup giornalieri cifrati

### Contatti

**Titolare del trattamento**: [INSERIRE NOME AZIENDA] — [INSERIRE INDIRIZZO]
**Email Privacy**: [INSERIRE EMAIL PRIVACY DEL CLIENTE]
**Responsabile tecnico (Data Processor)**: TotalKlick srls — privacy@totalklick.it

Ultima modifica: [INSERIRE DATA AGGIORNAMENTO]
`;


export default function LegalPrivacyTemplateWidget() {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(TEMPLATE);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        } catch (_e) { /* ignore */ }
    };

    return (
        <div className="min-h-screen" style={{ background: "var(--cream)" }}>
            <div className="max-w-3xl mx-auto px-5 py-12">
                <Link to="/" className="mono text-[11px] uppercase tracking-[0.2em] opacity-60 hover:opacity-100" data-testid="legal-back-home">← TotalKlick</Link>

                <div className="mt-6 mb-8">
                    <div className="mono text-[10px] uppercase tracking-[0.25em] opacity-65" style={{ color: "var(--mod-accent)" }}>Legal · Privacy Template</div>
                    <h1 className="text-[2.2rem] sm:text-[2.8rem] font-thin tracking-tight mt-1" style={{ color: "var(--navy)", fontFamily: "Geist, sans-serif", letterSpacing: "-0.03em" }}>
                        Template Privacy Policy<br />— per chi installa Aria
                    </h1>
                    <p className="mt-4 text-[14.5px] opacity-75" style={{ fontFamily: "Outfit, sans-serif" }}>
                        Copia questo testo e incollalo nella tua Privacy Policy. Sostituisci i campi <code>[INSERIRE …]</code> con i tuoi dati.
                        Questo template integra le informazioni richieste dagli <b>artt. 13-14 GDPR</b> per dichiarare l'uso del widget Aria sul tuo sito.
                    </p>
                </div>

                <div className="card-tpl p-5 mb-4 flex items-center justify-between flex-wrap gap-3" style={{ background: "white" }}>
                    <div>
                        <div className="mono text-[10px] uppercase tracking-wider font-extrabold opacity-65 mb-1">Cosa fare</div>
                        <ol className="text-[13px] list-decimal ml-5 space-y-1" style={{ fontFamily: "Outfit, sans-serif" }}>
                            <li>Copia il template qui sotto</li>
                            <li>Incollalo nella tua Privacy Policy (sezione "Strumenti di terze parti")</li>
                            <li>Sostituisci i campi <code>[INSERIRE …]</code> con i tuoi dati reali</li>
                            <li>Aggiorna la data dell'ultima modifica</li>
                            <li>Pubblica la nuova versione e linkala in footer + nel form Aria</li>
                        </ol>
                    </div>
                    <button
                        onClick={copy}
                        className="px-4 py-2 rounded-full mono text-[11px] uppercase tracking-wider font-bold transition"
                        style={{ background: "var(--navy)", color: "white", border: "1px solid var(--navy)" }}
                        data-testid="copy-privacy-template"
                    >
                        {copied ? "✓ Copiato" : "Copia template"}
                    </button>
                </div>

                <div className="card-tpl p-6" style={{ background: "white", borderColor: "var(--border)" }}>
                    <pre className="text-[12.5px] whitespace-pre-wrap font-mono leading-[1.7]" style={{ fontFamily: "ui-monospace, Menlo, Monaco, monospace", color: "var(--text-1)" }}>
                        {TEMPLATE}
                    </pre>
                </div>

                <div className="mt-8 p-5 rounded-2xl" style={{ background: "rgba(0,0,0,0.04)", border: "1px dashed var(--border)" }}>
                    <div className="mono text-[10px] uppercase tracking-wider font-extrabold opacity-65 mb-2">Disclaimer legale</div>
                    <p className="text-[12.5px] opacity-75" style={{ fontFamily: "Outfit, sans-serif" }}>
                        Questo template è fornito a titolo informativo e copre l'uso standard del widget Aria.
                        Se tratti <b>categorie particolari di dati</b> (Art. 9 GDPR: salute, religione, orientamento sessuale, dati biometrici),
                        o se operi in settori regolamentati (sanità, finanza, minori), <b>fai verificare il testo dal tuo DPO o consulente legale</b> prima di pubblicarlo.
                        TotalKlick srls non assume responsabilità per usi non conformi del template.
                    </p>
                </div>

                <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
                    <Link to="/legal/dpa-aria-widget" className="mono text-[11px] uppercase tracking-wider opacity-75 hover:opacity-100 underline" data-testid="link-dpa">→ Vedi anche: DPA Aria Widget</Link>
                    <Link to="/" className="mono text-[11px] uppercase tracking-wider opacity-60 hover:opacity-100">Torna a TotalKlick</Link>
                </div>
            </div>
        </div>
    );
}
